import { __awaiter } from "tslib";
import axios from 'axios';
import { StreamingClientErrorTypes } from './types/interfaces';
import { EventEmitter } from 'events';
import { StreamingClientError, retryPromise } from './utils';
export class AlertingLeaderExtension extends EventEmitter {
    constructor(client, options) {
        var _a;
        super();
        this.client = client;
        this.leaderStatus = {};
        this.alertableInteractionTypes = (_a = options.alertableInteractionTypes) !== null && _a !== void 0 ? _a : [];
    }
    handleStanzaInstanceChange(stanzaInstance) {
        var _a, _b;
        this.connectionId = (_b = (_a = stanzaInstance.transport) === null || _a === void 0 ? void 0 : _a.stream) === null || _b === void 0 ? void 0 : _b.id;
        this.setupAlertingLeader();
    }
    setupAlertingLeader() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.alertableInteractionTypes.length !== 0) {
                try {
                    this.getAlertingLeaderEarly();
                    yield this.subscribeToAlertingLeader();
                    yield this.markAsAlertable();
                    yield this.getAlertingLeader();
                }
                catch (err) {
                    this.client.logger.warn('Failed to setup alerting leader; falling back to acting as the leader');
                    // Fail 'open' so users don't miss calls
                    this.leaderStatus = { voice: { alerting: true, configured: false } };
                    this.emit('alertingLeaderChanged', this.leaderStatus);
                }
            }
        });
    }
    subscribeToAlertingLeader() {
        return __awaiter(this, void 0, void 0, function* () {
            const topic = `v2.users.${this.client.config.userId}.alertingleader`;
            this.client.on(`notify:${topic}`, (event) => {
                var _a, _b;
                (_a = this.getLeaderAbortController) === null || _a === void 0 ? void 0 : _a.abort();
                if ((_b = event.eventBody) === null || _b === void 0 ? void 0 : _b.connectionId) {
                    // The consuming client should be the one alerting if our connection is the current leader
                    const alerting = event.eventBody.connectionId === this.connectionId;
                    const clientType = event.eventBody.clientType;
                    let voice = { alerting, configured: true };
                    if (clientType) {
                        voice = Object.assign(Object.assign({}, voice), { clientType });
                    }
                    this.leaderStatus = { voice };
                    this.emit('alertingLeaderChanged', this.leaderStatus);
                }
            });
            return this.client._notifications._subscribeInternal(topic);
        });
    }
    markAsAlertable() {
        return __awaiter(this, void 0, void 0, function* () {
            const userId = this.client.config.userId;
            const connectionsRequestOptions = {
                method: 'patch',
                host: this.client.config.apiHost,
                authToken: this.client.config.authToken,
                logger: this.client.logger,
                data: {
                    alertable: true
                }
            };
            // STREAM-1204
            // There's a race condition between the backend service knowing about the connection
            // and us marking the connection as alertable. For now, we'll just retry with some delay.
            const maxRetries = 16;
            let retryCount = 0;
            const retry = retryPromise(() => this.client.http.requestApi(`apps/users/${userId}/connections/${this.connectionId}`, connectionsRequestOptions), () => {
                retryCount++;
                if (retryCount >= maxRetries) {
                    this.client.logger.info('Max retries reached for marking connection as alertable');
                    return false;
                }
                return true;
            }, 500, this.client.logger);
            return retry.promise
                .catch(() => {
                this.client.logger.warn('Could not mark this connection as alertable');
            });
        });
    }
    getAlertingLeaderEarly() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.getAlertingLeader();
            }
            catch (error) {
                if (axios.isAxiosError(error) && error.status === 400) {
                    this.client.logger.info('The org has not configured alerting leader functionality or there are not yet any active alertable connections; falling back to acting as the leader');
                    this.leaderStatus = { voice: { alerting: true, configured: false } };
                    this.emit('alertingLeaderChanged', this.leaderStatus);
                }
            }
        });
    }
    getAlertingLeader() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // If an early request is still in-flight, cancel it and get more recent data
            (_a = this.getLeaderAbortController) === null || _a === void 0 ? void 0 : _a.abort();
            this.getLeaderAbortController = new AbortController();
            const leaderRequestOptions = {
                method: 'get',
                host: this.client.config.apiHost,
                authToken: this.client.config.authToken,
                logger: this.client.logger,
                signal: this.getLeaderAbortController.signal
            };
            try {
                const currentLeader = yield this.client.http.requestApiWithRetry('users/alertingleader', leaderRequestOptions, 1000).promise;
                // The consuming client should be the one alerting if our connection is the current leader
                const alerting = currentLeader.data.connectionId === this.connectionId;
                const clientType = currentLeader.data.clientType;
                let voice = { alerting, configured: true };
                if (clientType) {
                    voice = Object.assign(Object.assign({}, voice), { clientType });
                }
                this.leaderStatus = { voice };
                this.emit('alertingLeaderChanged', this.leaderStatus);
            }
            catch (err) {
                if (axios.isCancel(err)) {
                    return;
                }
                throw err;
            }
        });
    }
    claimAlertingLeader() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.alertableInteractionTypes.length === 0) {
                this.client.logger.info('This client is not configured for any alertable interactions and will not attempt to claim alerting leader');
                throw new StreamingClientError(StreamingClientErrorTypes.generic, 'Unable to claim alerting leader; this client is not configured for any alertable interactions');
            }
            const leaderRequestOptions = {
                method: 'put',
                host: this.client.config.apiHost,
                authToken: this.client.config.authToken,
                logger: this.client.logger,
                data: {
                    connectionId: this.connectionId
                }
            };
            return this.client.http.requestApi('users/alertingleader', leaderRequestOptions)
                .catch((err) => {
                this.client.logger.warn('Unable to claim alerting leader; this client may not alert for incoming interactions');
                throw new StreamingClientError(StreamingClientErrorTypes.generic, 'Unable to claim alerting leader', err);
            });
        });
    }
    get expose() {
        return {
            on: this.on.bind(this),
            off: this.off.bind(this),
            claimAlertingLeader: this.claimAlertingLeader.bind(this),
            getLeaderStatus: () => { return this.leaderStatus; },
            leaderStatus: this.leaderStatus
        };
    }
}
